// @ts-nocheck
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Autocomplete, Box, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateScheduledTaskCache, useGetEquipmentForWorkOrder } from 'src/api/scheduledTask'
import { CenteredSpinner } from 'src/components/Spinner'
import { MUIThemeColors } from 'src/mui-theme'
import { getUTCDate } from 'src/utils/function'
import { z } from 'zod'
import { useGetCustomerUsers } from '../../../../api/customer'
import { invalidateTaskCache, useGetTask, useUpdateTask } from '../../../../api/tasks'
import { AppTextField } from '../../../../components/AppTextField'
import BasicDatePicker from '../../../../components/DateTimePicker'
import {
  AppForm,
  FormFileCoreUpload,
  FormSelectField,
  FormSubmitButton,
  FormTextField,
} from '../../../../components/FormCore'
import { CaptionedCell } from '../../../../components/table/CaptionedCell'
import { INVALID_END_DATE } from '../../../../constant'
import { TaskTypes } from '../../../../enums'
import { IUser } from '../../../../generated-types'
import { IMainComponent } from '../../../../generated-types/main-component'
import { useZodForm } from '../../../../hooks/zod-form'
import { getThemeColor, getTranslatedText } from '../../../../utils'
import { EdgeCase, getUserDetail, UserDetail } from '../../../../utils/getUserDetails'
import { taskIntervalList } from './task-constants'
import { durationTimeline } from './TaskForm'

interface TaskEditFormProp {
  initialValues?: any
  customerId: EntityId
  pop: any
  tasksId?: EntityId
  localityId?: EntityId
}

const TaskEditForm: React.FC<TaskEditFormProp> = ({ initialValues, customerId, pop, tasksId, localityId }) => {
  const { t: translate } = useTranslation(['locality', 'common'])
  const theme = useTheme()
  const { data: customerUsers } = useGetCustomerUsers(customerId as EntityId)
  const { data: task, isLoading } = useGetTask(localityId as EntityId, tasksId as EntityId)
  const { mutateAsync } = useUpdateTask(initialValues?.localityId ?? task?.localityId, initialValues?.id ?? task?.id)
  const { data: equipments } = useGetEquipmentForWorkOrder((localityId as EntityId) ?? initialValues?.localityId)
  const formType = useMemo(() => initialValues?.type ?? task?.type, [initialValues, task])
  const [selectedStartDate, setSelectedStartDate] = useState<Date>()
  const [isDurationDisabled, setIisDurationDisabled] = useState(initialValues?.interval == 0)
  const [selectedEquipments, setSelectedEquipments] = useState<IMainComponent[]>([])

  useEffect(() => {
    if (task) {
      form.reset({
        ...task,
        ...{
          interval: taskIntervalList.find(ti => ti.value == task?.interval),
          fileIds: task?.files?.map(f => f.id),
          equipmentIds: task?.equipmentIds,
        },
      })
    }
  }, [task])

  useEffect(() => {
    const equipmentIds = task?.equipmentIds || initialValues?.equipmentIds
    if (equipmentIds && equipments) {
      const selectedEquips = equipments.filter(eq => equipmentIds.includes(eq.id))
      setSelectedEquipments(selectedEquips as IMainComponent[])
    }
  }, [task, initialValues, equipments])

  const taskSchema = z
    .object({
      title: z
        .string()
        .nonempty(translate('nameIsRequired', { ns: 'common' }))
        .max(200),
      description: z
        .string()
        .nonempty(translate('descriptionIsRequired', { ns: 'common' }))
        .max(400),
      responsibleUserId: z.string().optional().nullable(),
      startDate: z.date().or(z.string().transform(v => getUTCDate(new Date(v)) || new Date(v))),
      endDate: z
        .date()
        .or(z.string().transform(v => getUTCDate(new Date(v)) || new Date(v)))
        .optional()
        .nullable(),
      duration: z
        .number()
        .or(z.string().transform(v => parseInt(v)))
        .refine(s => s > 0, { message: translate('required', { ns: 'common' }) }),
      fileIds: z
        .array(z.union([z.string(), z.number()]))
        .nullable()
        .optional(),
      files: z.array(z.any()).nullable().optional(),
      equipmentIds: z.array(z.number()).nullable().optional(),
      interval: z
        .object({
          value: z.number(),
        })
        .optional()
        .refine(data => formType == TaskTypes.Once || (formType == TaskTypes.Repeatable && data != null), {
          message: translate('required', { ns: 'common' }),
        }),
      type: z.number(),
    })
    .refine(data => !data.endDate || data.endDate > data.startDate, {
      message: translate('endStartDateError', { ns: 'common' }),
      path: ['endDate'],
    })
    .refine(
      form => {
        // Kept out of the comparison: a cast directly left of `<` reads as the start of type
        // arguments once Babel's dev-mode reprint has dropped the parentheses around it.
        const endDate = form.endDate as Date | null
        const startDate = form.startDate as Date
        return !(endDate != null && endDate < startDate)
      },
      {
        message: translate('endStartDateError', { ns: 'common' }),
      },
    )
    .refine(data => data.type == TaskTypes.Repeatable || data.duration, {
      message: translate('required', { ns: 'common' }),
      path: ['duration'],
    })
    .refine(
      data =>
        data.interval == undefined || data.interval.value < 1 || data.duration <= durationTimeline[data.interval.value],
      {
        message: translate('duration-validation', { ns: 'task' }),
        path: ['duration'],
      },
    )

  const form = useZodForm(taskSchema, {
    defaultValues: {
      ...initialValues,
      ...{ interval: taskIntervalList.find(ti => ti.value == initialValues?.interval) },
    },
  })

  const onSubmit = useCallback(
    async values => {
      values.interval = values?.interval?.value
      values.files = null
      values.equipmentIds = selectedEquipments.map(eq => eq.id)
      await mutateAsync(
        { ...(initialValues ?? task), ...values },
        {
          onSuccess: () => {
            toast.success(translate('taskUpdatedSuccessfully', { ns: 'task' }))
            invalidateTaskCache.getCustomerAdminTaskByFilter(customerId)
            invalidateScheduledTaskCache.useSingleScheduledTask(
              initialValues?.localityId ?? task?.localityId,
              undefined,
            )
            if (task?.localityId) {
              invalidateScheduledTaskCache.useScheduledTasks(task?.localityId as EntityId)
              invalidateTaskCache.useGetTask(task?.localityId, task?.id)
            }
            invalidateScheduledTaskCache.invalidateAllScheduledTasks(localityId as EntityId)
            invalidateScheduledTaskCache.useGetEquipmentOfWorkOrder()
            invalidateScheduledTaskCache.useGetEquipmentForWorkOrder(undefined, localityId)
            pop()
          },
          onError: error => {
            if (error['data'] == INVALID_END_DATE) {
              toast.error(translate('invalid-end-date', { ns: 'task' }))
            } else {
              toast.error(translate('updatingError', { ns: 'common' }))
            }
          },
        },
      )
    },
    [customerId, initialValues, mutateAsync, pop, translate, selectedEquipments],
  )

  useEffect(() => {
    setSelectedStartDate(initialValues?.startDate)
  }, [initialValues?.startDate])

  if (isLoading) return <CenteredSpinner />
  return (
    <AppForm form={form} onSubmit={onSubmit} hasInitialValues={initialValues}>
      <AppTextField
        fullWidth
        disabled
        label={translate('locality', { ns: 'common' })}
        defaultValue={initialValues?.locality?.name ?? task?.locality?.name}
      />
      <FormTextField name="title" label={translate('title', { ns: 'common' })} defaultValue={initialValues?.title} />
      <FormTextField name="description" label={translate('description', { ns: 'common' })} multiline rows={3} />
      {customerUsers && (
        <FormSelectField
          name="responsibleUser"
          data={customerUsers!}
          label={translate('responsibleUser', { ns: 'locality' })}
          getOptionLabel={option => getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
          renderOption={(props: any, option) => (
            <Box {...props} key={option.id}>
              {getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
            </Box>
          )}
          defaultValue={initialValues?.responsibleUser}
          clearOnBlur={false}
          clearOnEscape={false}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(e, value) => {
            form.setValue('responsibleUser', value)
            form.setValue('responsibleUserId', (value as IUser)?.id ?? null)
          }}
        />
      )}
      {formType == TaskTypes.Repeatable && (
        <FormSelectField
          name="interval"
          data={taskIntervalList}
          disablePortal={false}
          label={translate('interval', { ns: 'task' })}
          getOptionLabel={option => option.option}
          renderOption={(props: any, option) => (
            <Box {...props} key={option.value}>
              {option.option}
            </Box>
          )}
          clearOnBlur={false}
          clearOnEscape={false}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onChange={(eve, value) => {
            form.setValue('interval', value as any)
            if (value == taskIntervalList[0]) {
              setIisDurationDisabled(true)
              form.setValue('duration', 1)
            } else {
              setIisDurationDisabled(false)
            }
          }}
        />
      )}
      <BasicDatePicker
        name="startDate"
        label={translate('startDate', { ns: 'common' })}
        onChange={date => {
          form.setValue('startDate', date)
          setSelectedStartDate(date)
        }}
      />
      {formType == TaskTypes.Repeatable && (
        <BasicDatePicker
          name="endDate"
          label={translate('endDate', { ns: 'common' })}
          minDate={selectedStartDate}
          allowNullable
        />
      )}
      <FormTextField
        type="number"
        name="duration"
        label={translate('duration', { ns: 'task' })}
        InputProps={{
          readOnly: isDurationDisabled,
        }}
        focused={!!form.getValues('duration')}
      />
      {formType == TaskTypes.Repeatable && (
        <>
          <Autocomplete<IMainComponent, true>
            options={(equipments || []) as IMainComponent[]}
            autoHighlight
            getOptionLabel={o => `${getTranslatedText(o.typeName)} | ${o.serialNumber}` || ''}
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.id}>
                <CaptionedCell primary={getTranslatedText(o.typeName)} caption={o.serialNumber} />
              </Box>
            )}
            multiple
            disableCloseOnSelect
            value={selectedEquipments || []}
            renderInput={params => (
              <AppTextField
                {...params}
                label={translate('default-equipment', { ns: 'task' })}
                inputProps={{ ...params.inputProps }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => {
              setSelectedEquipments(value)
            }}
          />
          <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ mx: 1 }}>
            <InfoOutlinedIcon
              sx={{ fontSize: 14, color: getThemeColor(theme, MUIThemeColors.primaryMain), mt: '2px' }}
            />
            <Typography variant="caption">{translate('default-equipment-info', { ns: 'task' })}</Typography>
          </Box>
        </>
      )}
      <Box py={1}>
        <FormFileCoreUpload
          label={translate('fileName', { ns: 'common' })}
          name="fileIds"
          mapFileKey="id"
          initialFiles={initialValues?.files ?? task?.files}
          multiple
          height="150px"
        />
      </Box>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'end'}>
        <FormSubmitButton createText={translate('update', { ns: 'common' })} />
      </Box>
    </AppForm>
  )
}

export default TaskEditForm
